/* eslint-disable max-lines, max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import { get, includes, keyBy, orderBy } from "lodash";
import React from "react";
import ReactQuill from "react-quill";

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";

import {
  ChangeWaitlistUpdateSettings,
  ChangeWaitlistUpdateSettingsVariables,
  FetchWaitlistUpdateSettings,
  FetchWaitlistUpdateSettingsVariables,
  ToggleWaitlistUpdate,
  ToggleWaitlistUpdateVariables,
  UserRole,
} from "types/gql-types";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Viewer } from "components/auth/types";
import { VIEWER_QUERY } from "components/auth/query";
import { useSnackbar } from "components/haven-design/snackbar";
import { apolloClient } from "data/graphql";
import { SettingsHeader } from "../settings-header";
import styles from "./index.module.css";
import {
  CHANGE_WAITLIST_UPDATE_SETTINGS,
  TOGGLE_WAITLIST_UPDATE_FOR_LIST,
  WAITLIST_UPDATE_SETTINGS,
} from "./query";

type MessageType = "SMS" | "LETTER" | "EMAIL";

enum LetterMode {
  All = "all",
  MailingOnly = "mailingOnly",
  None = "none",
}

const LETTER_MODE_OPTIONS = [
  { label: "Send letters to all applicants", value: LetterMode.All },
  { label: "Only send letters to applicants with only a mailing address", value: LetterMode.MailingOnly },
  { label: "Do not send any letters", value: LetterMode.None },
];

type Settings = FetchWaitlistUpdateSettings["organization"]["scheduledUpdateRequests"]["settings"];

const isNotAuthorized = (viewer: Viewer): boolean => !includes(viewer.roles, UserRole.SUPERVISOR);

const getTemplateDelta = (data: FetchWaitlistUpdateSettings | undefined, type: MessageType) => {
  const templates = get(data, ["organization", "scheduledUpdateRequests", "templates"], []);
  const keyed = keyBy(templates, (t) => t.type);
  const delta = get(keyed, [type, "body"], false);
  if (!delta) return "";

  try {
    return JSON.parse(delta);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("getTemplateDelta failed for type", type, e);
    return "";
  }
};

const PreviewComponent: React.FC<{
  data: FetchWaitlistUpdateSettings | undefined;
  loading?: boolean;
}> = ({ data, loading }) => {
  const [messageType, setMessageType] = React.useState<MessageType>("SMS");

  return <div className={styles.preview}>
    <SettingsHeader title="Message preview" />
    {loading ? (
      <>
        <div className={styles.previewTypes}>
          <Skeleton variant="rectangular" width={64} height={24} style={{ display: "inline-block", marginRight: 8 }} />
          <Skeleton variant="rectangular" width={64} height={24} style={{ display: "inline-block", marginRight: 8 }} />
          <Skeleton variant="rectangular" width={64} height={24} style={{ display: "inline-block" }} />
        </div>
        <div>
          <Skeleton variant="rectangular" height={300} />
        </div>
      </>
    ) : (
      <>
        <div className={styles.previewTypes}>
          {["Letter", "Email", "SMS"].map((type) => (
            <a
              href=""
              key={type}
              onClick={(event) => {
                event.preventDefault();
                setMessageType(type.toUpperCase() as MessageType);
              }}
            >
              {type}
            </a>
          ))}
        </div>
        <div>
          <ReactQuill
            readOnly
            modules={{
              toolbar: false,
            }}
            value={getTemplateDelta(data, messageType)}
          />
        </div>
      </>
    )}
  </div>;
};

const AutomaticUpdatePropertySelectionForm: React.FC<{
  viewer: Viewer;
  refetch: () => void;
  interestLists: Array<{
    id: string;
    name: string;
    type: string;
    scheduledUpdate: { enabled?: boolean | null };
  }>;
  loading?: boolean;
}> = ({ viewer, refetch, interestLists, loading }) => {
  const [toggleUpdate] = useMutation<ToggleWaitlistUpdate, ToggleWaitlistUpdateVariables>(
    TOGGLE_WAITLIST_UPDATE_FOR_LIST,
  );
  return <div>
    <p>
      Enable the properties that you want automatic list updates for.
    </p>
    {loading ? (
      Array.from({ length: 6 }).map((_, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className={styles.label}>
          <Skeleton variant="rectangular" height={28} width={260} />
        </div>
      ))
    ) : (
      orderBy(interestLists, ["name"], ["asc"])
        .filter(({ type }) => type === "property" || type === "interest")
        .filter(({ id }) => viewer.lists?.some((list: { id: string }) => list.id === id))
        .map(({ id, name, scheduledUpdate: { enabled } }) => (
          <div key={id}>
            <FormControlLabel
              className={styles.label}
              control={
                <Checkbox
                  checked={enabled || false}
                  disabled={isNotAuthorized(viewer)}
                  onChange={async () => {
                    await toggleUpdate({ variables: { id, enabled: !enabled } });
                    await apolloClient.refetchQueries({
                      include: [VIEWER_QUERY],
                    });
                    refetch();
                  }}
                />
              }
              label={name}
            />
          </div>
        ))
    )}
  </div>;
};

const UpdateSettingsFormComponent: React.FC<{
  viewer: Viewer;
  refetch: () => void;
  settings: Settings;
  loading?: boolean;
}> = ({ viewer, refetch, settings, loading }) => {
  const { openSnackbar } = useSnackbar();
  const [changeSettings, { loading: savingLetterMode }] = useMutation<
  ChangeWaitlistUpdateSettings,
  ChangeWaitlistUpdateSettingsVariables
  >(CHANGE_WAITLIST_UPDATE_SETTINGS);

  const serverLetterMode = settings.letterMode ?? LetterMode.All;
  const [letterMode, setLetterMode] = React.useState(serverLetterMode);
  React.useEffect(() => {
    setLetterMode(serverLetterMode);
  }, [serverLetterMode]);

  if (loading) {
    return (
      <div>
        <Skeleton variant="rectangular" height={40} width={320} />
        <div style={{ marginTop: 12 }}>
          <Skeleton variant="rectangular" height={18} width={420} />
          <Skeleton variant="rectangular" height={18} width={360} />
        </div>
      </div>
    );
  }

  const selectDisabled = isNotAuthorized(viewer) || savingLetterMode;

  return (
    <>
      <FormControl fullWidth variant="outlined" margin="normal" disabled={selectDisabled}>
        <InputLabel id="scheduled-updates-letter-mode-label">Letter mode</InputLabel>
        <Select
          labelId="scheduled-updates-letter-mode-label"
          label="Letter mode"
          value={letterMode}
          onChange={async (e) => {
            const next = e.target.value as LetterMode;
            const previous = letterMode;
            setLetterMode(next);
            try {
              await changeSettings({
                variables: {
                  id: viewer.organizationId as string,
                  options: { letterMode: next },
                },
              });
              await refetch();
              openSnackbar({ message: "Letter mode updated", status: "success" });
            } catch {
              setLetterMode(previous);
              openSnackbar({ message: "Could not update letter mode. Try again.", status: "error", duration: 4000 });
            }
          }}
        >
          {LETTER_MODE_OPTIONS.map(({ label, value }) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {letterMode === LetterMode.None && (
        <div>
          <strong>Note: </strong> Not all applicants will be contacted with this option.
          You will be responsible for calling each applicant without an email or cell
          phone individually.
          <br />
          <br />
        </div>
      )}
    </>
  );
};

const ScheduledUpdates: React.FC<{ viewer: Viewer }> = (props) => {
  if (!props.viewer) return null;
  if (!props.viewer.organizationId) return null;

  const vars = { variables: { id: props.viewer.organizationId } };
  const { loading, error, data, refetch } = useQuery<
  FetchWaitlistUpdateSettings,
  FetchWaitlistUpdateSettingsVariables
  >(WAITLIST_UPDATE_SETTINGS, vars);
  if (error) return null;

  const isLoading = loading || !data;

  const {
    organization: {
      interestLists,
      scheduledUpdateRequests: { settings },
    },
  } = data || {
    organization: { interestLists: [], scheduledUpdateRequests: { settings: {} } },
  } as any;

  return (
    <div className={styles.content}>
      <div className={styles.settings}>
        <SettingsHeader title="List Update" />
        <div className={styles.description}>
          <p>
            With list updates enabled, Haven Connect will automatically send letters, texts, and
            emails to all active applicants on the checked waitlists below once a year (usually
            in the spring), directing them to update their income, household size, and contact
            information.
          </p>
        </div>

        <UpdateSettingsFormComponent
          viewer={props.viewer} refetch={refetch} settings={settings} loading={isLoading} />

        <AutomaticUpdatePropertySelectionForm
          viewer={props.viewer}
          refetch={refetch} interestLists={interestLists} loading={isLoading} />
      </div>

      <PreviewComponent data={data} loading={isLoading} />
    </div>
  );
};

export default authorizeForRole<Record<string, unknown>>(
  [UserRole.ADMIN, UserRole.PROPERTY_MANAGER, UserRole.SUPERVISOR],
  ScheduledUpdates as unknown as React.FC<Record<string, unknown>>,
);
